import {useMutation, UseMutationResult, useQueryClient} from "@tanstack/react-query";
import {ITableDataEntry} from "components/dashboard/product-template-table/utils/interfaces";
import {APIResponse} from "services/HttpService";
import {ProductTemplatesApi} from "../ProductTemplatesApi";
import {
    defaultMutationOptions,
    defaultResponseHandler,
    IMutationOptions,
    IQueryOptions,
    QueryKey,
    useQueryWrapper
} from "./api-hooks-common";

type ProductTemplateEntryType = {
    safeName: string,
    dateFrom?: string,
    dateTo?: string,
    filter?: string
}

export const useGetProductTemplateInfos = () => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_INFOS], () => ProductTemplatesApi.getProductTemplateInfos());
export const useGetProductTemplateInfo = (id: number | null, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_INFOS, id?.toString()], () => ProductTemplatesApi.getProductTemplateInfo(id), null, options);

export const useGetProductTemplateConfig = (refName: string, id: string, params?: {
    usePromiseHandler?: (((res: APIResponse<any>) => any) | null | undefined)
}) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_CONFIGS, refName, id], () => ProductTemplatesApi.getProductTemplateConfig(refName), params?.usePromiseHandler);

export const useGetProductTemplateData = (params: {
    safeName: string,
    dateFrom?: string,
    dateTo?: string,
    filter?: string
}, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], () => ProductTemplatesApi.getProductTemplateData(params), null, options);

export const useGetProductTemplateFilterPreviewData = (params: {
    safeName: string,
    dateFrom?: string,
    dateTo?: string,
    filter?: string
}, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], () => ProductTemplatesApi.getProductTemplateDataFilterPreview(params), null, options);

export const useGetProductTemplateForeignKeys = (templateName: string, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_FOREIGN_KEYS, templateName], () => ProductTemplatesApi.getProductTemplateForeignKeys(templateName), null, options);

export function useUpdateProductTemplateEntry(params: ProductTemplateEntryType, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, {
    entryId: string,
    body: any
}> {
    const options = {...defaultMutationOptions, ...specOptions};
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ((data: {
            entryId: string,
            body: any
        }) => ProductTemplatesApi.updateProductTemplateEntry({safeName: params.safeName, ...data}).then(defaultResponseHandler)),
        onError: error => {
            options.onFailure!!(error);
        },
        onMutate: variables => {
            options.onMutate!!(variables);
        },
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], (oldQueryData: any) => {
                return oldQueryData.map((obj: any) => {
                    if (obj.id === data.id) {
                        return data;
                    } else {
                        return obj;
                    }
                });
            });
            options.onSuccess!!();
        }
    });
}

export function useDeleteProductTemplateEntry(params: ProductTemplateEntryType, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, {
    entryId: string
}> {
    const options = {...defaultMutationOptions, ...specOptions};
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ((data: {
            entryId: string
        }) => ProductTemplatesApi.deleteProductTemplateEntry(params.safeName, data.entryId).then(defaultResponseHandler)),
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], (oldQueryData: any) => {
                return oldQueryData.filter((e: ITableDataEntry) => e.id !== data.id);
            });

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error);
        },
        onMutate: variables => {
            options.onMutate!!(variables);
        }
    });
}

export function useCreateProductTemplateEntry(params: ProductTemplateEntryType, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, {
    body: any
}> {
    const options = {...defaultMutationOptions, ...specOptions};
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ((data: {
            body: any,
        }) => ProductTemplatesApi.createProductTemplateEntry(params.safeName, data.body).then(defaultResponseHandler)),
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], (oldQueryData: any) => {
                return [
                    data,
                    ...oldQueryData,
                ];
            });

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error);
        },
        onMutate: variables => {
            options.onMutate!!(variables);
        }
    });
}

export function useSignProductTemplateEntry(params: ProductTemplateEntryType, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, {
    entryId: string,
    referenceName: string
}> {
    const options = {...defaultMutationOptions, ...specOptions};
    const queryClient = useQueryClient();
    let entryId = "";
    let referenceName = "";
    return useMutation({
            mutationFn: (signData: {
                entryId: string,
                referenceName: string
            }) => (ProductTemplatesApi.signProductTemplateEntry(params.safeName, signData.entryId, signData.referenceName).then(defaultResponseHandler))
                .finally(() => {
                    entryId = signData.entryId;
                    referenceName = signData.referenceName;
                }),

            onSuccess: (newData) => {
                queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], (oldQueryData: any) => {
                    return oldQueryData.map((obj: any) => {
                        if (obj.id === entryId) {
                            if (obj.entries[referenceName] != null) {
                                obj.entries[referenceName].value = newData;
                            }
                        }
                        return obj;
                    });
                });
                options.onSuccess!!();
            },
            onError: error => {
                options.onFailure!!(error);
            },
            onMutate: variables => {
                options.onMutate!!(variables);
            }
        }
    );
}

export function useDeleteProductTemplateEntriesByFilter(params: ProductTemplateEntryType, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, {
    dateFrom?: string,
    dateTo?: string,
    filter?: string
}> {
    const options = {...defaultMutationOptions, ...specOptions};
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ((data) => ProductTemplatesApi.deleteProductTemplateEntriesByFilter(params.safeName, data).then(defaultResponseHandler)),
        onSuccess: () => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], (oldQueryData: any) => {
                return [];
            });

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error);
        },
        onMutate: variables => {
            options.onMutate!!(variables);
        }
    });
}

export function useWipeAllProductTemplateEntries(params: ProductTemplateEntryType, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, unknown> {
    const options = {...defaultMutationOptions, ...specOptions};
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (() => ProductTemplatesApi.wipeAllProductTemplateEntries(params.safeName).then(defaultResponseHandler)),
        onSuccess: () => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA, params.safeName, params.dateFrom, params.dateTo, params.filter], (oldQueryData: any) => {
                return [];
            });

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error);
        },
        onMutate: variables => {
            options.onMutate!!(variables);
        }
    });
}